/** Protected runway geometry and explicit ground crossing permissions. */
import { alongTrackM, crossTrackM, distanceM, type AirportData, type GroundNetwork, type LatLon, type RunwayEnd } from '@atc/core';
import type { Aircraft } from './aircraft';

export const PROTECTED_HALF_WIDTH_M = 75;
export function inRunwayArea(airport: AirportData, rw: RunwayEnd, point: LatLon): boolean {
  const length = airport.runways.find((r) => r.ends.some((e) => e.name === rw.name))?.lengthM ?? 0;
  const along = alongTrackM(rw.lat, rw.lon, rw.headingDeg, point.lat, point.lon);
  return along >= -30 && along <= length + 30 && Math.abs(crossTrackM(rw.lat, rw.lon, rw.headingDeg, point.lat, point.lon)) <= PROTECTED_HALF_WIDTH_M;
}

/** Test a taxi segment against the full runway rectangle, including endpoints. */
export function crossingRunway(airport: AirportData, start: LatLon, end: LatLon): RunwayEnd | undefined {
  return airport.runways.find((r) => {
    const rw = r.ends[0];
    const x1 = alongTrackM(rw.lat, rw.lon, rw.headingDeg, start.lat, start.lon), x2 = alongTrackM(rw.lat, rw.lon, rw.headingDeg, end.lat, end.lon);
    const y1 = crossTrackM(rw.lat, rw.lon, rw.headingDeg, start.lat, start.lon), y2 = crossTrackM(rw.lat, rw.lon, rw.headingDeg, end.lat, end.lon);
    let enter = 0, leave = 1;
    for (const [a, b, min, max] of [[x1, x2, -30, r.lengthM + 30], [y1, y2, -PROTECTED_HALF_WIDTH_M, PROTECTED_HALF_WIDTH_M]]) {
      const delta = b! - a!;
      if (Math.abs(delta) < 0.001) { if (a! < min! || a! > max!) return false; continue; }
      const t1 = (min! - a!) / delta, t2 = (max! - a!) / delta;
      enter = Math.max(enter, Math.min(t1, t2)); leave = Math.min(leave, Math.max(t1, t2));
      if (enter > leave) return false;
    }
    return true;
  })?.ends[0];
}

export function updateSurfaceClearance(ac: Aircraft, airport: AirportData): void {
  if (!ac.control || ac.phase !== 'taxiOut') return;
  const next = ac.taxiRoute?.[ac.taxiRouteIdx ?? 0];
  if (!next) return;
  const current = ac.crossing;
  if (current?.approved && current.entered) {
    const rw = airport.runways.flatMap((r) => r.ends).find((e) => e.name === current.runway)!;
    if (!inRunwayArea(airport, rw, ac) && !crossingRunway(airport, ac, next)) ac.crossing = undefined;
  }
  const rw = crossingRunway(airport, ac, next);
  if (!rw) return;
  if (ac.crossing?.runway !== rw.name) ac.crossing = { runway: rw.name, approved: false, entered: false };
  if (ac.crossing?.approved && inRunwayArea(airport, rw, ac)) ac.crossing.entered = true;
}

/** Select an actual connected taxiway exit ahead of touchdown, never a turn across grass. */
export function runwayExit(airport: AirportData, rw: RunwayEnd, minimumAlongM: number): LatLon[] | undefined {
  const net = airport.groundNetwork;
  if (!net) return undefined;
  const candidates = net.nodes.filter((n) => {
    const along = alongTrackM(rw.lat, rw.lon, rw.headingDeg, n.lat, n.lon);
    return along >= minimumAlongM && inRunwayArea(airport, rw, n) && Math.abs(crossTrackM(rw.lat, rw.lon, rw.headingDeg, n.lat, n.lon)) < 25;
  }).sort((a, b) => alongTrackM(rw.lat, rw.lon, rw.headingDeg, a.lat, a.lon) - alongTrackM(rw.lat, rw.lon, rw.headingDeg, b.lat, b.lon));
  for (const start of candidates) {
    const queue: number[][] = [[start.id]], visited = new Set([start.id]);
    while (queue.length) {
      const path = queue.shift()!, node = net.nodes[path.at(-1)!]!;
      if (!inRunwayArea(airport, rw, node)) return path.map((id) => ({ lat: net.nodes[id]!.lat, lon: net.nodes[id]!.lon }));
      for (const edge of node.edges) {
        const next = net.nodes[edge.to];
        if (!next || visited.has(edge.to) || path.length > 15 || distanceM(start.lat, start.lon, next.lat, next.lon) > 350) continue;
        visited.add(edge.to); queue.push([...path, edge.to]);
      }
    }
  }
  return undefined;
}

export function nearestGroundNode(net: GroundNetwork, point: LatLon): number {
  return net.nodes.reduce((best, n) => distanceM(point.lat, point.lon, n.lat, n.lon) < distanceM(point.lat, point.lon, net.nodes[best]!.lat, net.nodes[best]!.lon) ? n.id : best, 0);
}

/** Shortest taxiway path between two graph nodes, both included; undefined when they are not connected. */
export function taxiPath(net: GroundNetwork, start: number, target: number): LatLon[] | undefined {
  const dist = new Array(net.nodes.length).fill(Infinity), prev = new Array<number>(net.nodes.length).fill(-1), open = new Set<number>([start]); dist[start] = 0;
  while (open.size) {
    let u = -1; for (const n of open) if (u < 0 || dist[n] < dist[u]) u = n;
    open.delete(u); if (u === target) break;
    for (const e of net.nodes[u]!.edges) if (dist[u] + e.distanceM < dist[e.to]) { dist[e.to] = dist[u] + e.distanceM; prev[e.to] = u; open.add(e.to); }
  }
  if (!Number.isFinite(dist[target])) return undefined;
  const ids: number[] = []; for (let n = target; n >= 0; n = prev[n]!) { ids.push(n); if (n === start) break; }
  return ids.at(-1) === start ? ids.reverse().map((id) => ({ lat: net.nodes[id]!.lat, lon: net.nodes[id]!.lon })) : undefined;
}
